import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@material-ui/core';

import BundleOperationDialog from '../../../../components/kea/BundleOperationDialog';

function isPending(status) {
  return status === 'queued' || status === 'running';
}

export default function PublishProgressDialog({
  open,
  operation,
  status,
  error,
  onClose,
  onRetry,
  onOpenPreview,
  canOpenPreview,
  localizeMessage,
}) {
  const pending = isPending(status);
  const success = status === 'success';
  const stale = status === 'stale';
  const failed = status === 'failed';
  const keyPrefix = operation === 'preview'
    ? 'operator.bundle.preview.dialog'
    : 'operator.bundle.publish.dialog';

  if (!pending && !success && !stale && !failed) {
    return null;
  }

  let dialogState = 'building';
  if (success) dialogState = 'success';
  if (stale) dialogState = 'warning';
  if (failed) dialogState = 'error';

  let title = localizeMessage(`${keyPrefix}.building.title`);
  let heading = localizeMessage(`${keyPrefix}.building.heading`);
  let description = localizeMessage(`${keyPrefix}.building.description`);
  let detail = null;
  let detailKind = null;

  if (success) {
    title = localizeMessage(`${keyPrefix}.success.title`);
    heading = localizeMessage(`${keyPrefix}.success.heading`);
    description = localizeMessage(`${keyPrefix}.success.description`);
  }

  if (stale) {
    title = localizeMessage('operator.home.publish.unpublished.title');
    heading = localizeMessage('operator.home.publish.unpublished.title');
    description = localizeMessage('operator.home.publish.unpublished.hint');
    detailKind = 'warning';
    detail = (
      <div data-testid="publish-progress-stale">
        {error || localizeMessage('operator.home.publish.unpublished.hint')}
      </div>
    );
  }

  if (failed) {
    title = localizeMessage(`${keyPrefix}.error.title`);
    heading = localizeMessage(`${keyPrefix}.error.heading`);
    description = localizeMessage(`${keyPrefix}.error.description`);
    detailKind = 'error';
    detail = (
      <div data-testid="publish-progress-error">
        {error || localizeMessage('searchInterface.build.errorHint')}
      </div>
    );
  }

  let actions = null;
  if (!pending) {
    const closeButton = (
      <Button
        key="close"
        variant={success && operation === 'production' ? 'contained' : 'outlined'}
        color={success && operation === 'production' ? 'primary' : 'default'}
        onClick={onClose}
        data-testid="publish-progress-close"
      >
        {localizeMessage('common.close')}
      </Button>
    );

    actions = (
      <>
        {closeButton}
        {failed && (
          <Button
            variant="contained"
            color="primary"
            onClick={onRetry}
            data-testid="publish-progress-retry"
          >
            {localizeMessage(`${keyPrefix}.error.cta`)}
          </Button>
        )}
        {success && operation === 'preview' && canOpenPreview && (
          <Button
            variant="contained"
            color="primary"
            onClick={onOpenPreview}
            data-testid="publish-progress-open-preview"
          >
            {localizeMessage('operator.bundle.preview.dialog.success.openPreview')}
          </Button>
        )}
      </>
    );
  }

  return (
    <BundleOperationDialog
      open={open}
      state={dialogState}
      title={title}
      heading={heading}
      description={description}
      detail={detail}
      detailKind={detailKind}
      actions={actions}
      onClose={onClose}
      closeLabel={localizeMessage('common.close')}
      closeTestId="publish-progress-dismiss"
      progressLabel={heading}
      progressTestId="publish-progress-spinner"
      testId="publish-progress-dialog"
    />
  );
}

PublishProgressDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  operation: PropTypes.oneOf(['preview', 'production']),
  status: PropTypes.string,
  error: PropTypes.string,
  onClose: PropTypes.func,
  onRetry: PropTypes.func,
  onOpenPreview: PropTypes.func,
  canOpenPreview: PropTypes.bool,
  localizeMessage: PropTypes.func.isRequired,
};

PublishProgressDialog.defaultProps = {
  operation: 'production',
  status: null,
  error: null,
  onClose: null,
  onRetry: null,
  onOpenPreview: null,
  canOpenPreview: false,
};
